import { useMemo } from "react";
import type {
  MediaMetadata,
  PlatformRequirements,
} from "../components/brandDetails/tabs/creator-studio/platforms/types";

const REQUIREMENTS: PlatformRequirements = {
  maxImages: 10,
  maxVideos: 1,
  imageFormats: "JPEG, PNG, GIF, WebP",
  videoFormats: "MP4, MOV",
  maxImageSize: "10 MB",
  maxVideoSize: "100 MB",
  videoDuration: "20 minutes",
  recommended: "1200 × 630 px (1.91:1)",
  minDimensions: "200 × 200 px",
  maxDimensions: "8192 × 8192 px",
  aspectRatios:
    "1.91:1 (landscape), 1:1 (square), 4:5 (portrait), 9:16 (story)",
  minWidth: 200,
  minHeight: 200,
  maxWidth: 8192,
  maxHeight: 8192,
};

export interface FacebookFormValidation {
  imageCountValid: boolean | null;
  imageSizeValid: boolean | null;
  dimensionsValid: boolean | null;
  aspectRatiosValid: boolean | null;
  videoCountValid: boolean | null;
  videoDurationValid: boolean | null;
}

export function useFacebookForm(mediaMetadata: MediaMetadata[]) {
  const maxImages =
    typeof REQUIREMENTS.maxImages === "number" ? REQUIREMENTS.maxImages : 10;

  const validation = useMemo((): FacebookFormValidation => {
    const enabledMedia = mediaMetadata.slice(0, maxImages);

    if (enabledMedia.length === 0) {
      return {
        imageCountValid: null,
        imageSizeValid: null,
        dimensionsValid: null,
        aspectRatiosValid: null,
        videoCountValid: null,
        videoDurationValid: null,
      };
    }

    const images = enabledMedia.filter((m) => m.type === "image");
    const videos = enabledMedia.filter((m) => m.type === "video");

    const imageCountValid =
      images.length === 0 ? null : images.length <= maxImages;

    const maxImageSizeBytes = 10 * 1024 * 1024;
    const imageSizeValid =
      images.length === 0
        ? null
        : images.every((img) => img.size <= maxImageSizeBytes);

    const dimensionsValid =
      images.length === 0
        ? null
        : images.every(
            (img) =>
              img.width >= (REQUIREMENTS.minWidth ?? 0) &&
              img.height >= (REQUIREMENTS.minHeight ?? 0) &&
              img.width <= (REQUIREMENTS.maxWidth ?? Infinity) &&
              img.height <= (REQUIREMENTS.maxHeight ?? Infinity)
          );

    const maxVideos =
      typeof REQUIREMENTS.maxVideos === "number"
        ? REQUIREMENTS.maxVideos
        : Infinity;
    const videoCountValid =
      videos.length === 0 ? null : videos.length <= maxVideos;

    const videoDurationValid =
      videos.length === 0
        ? null
        : videos.every((video) => (video.duration ?? 0) <= 1200);

    return {
      imageCountValid,
      imageSizeValid,
      dimensionsValid,
      aspectRatiosValid: null,
      videoCountValid,
      videoDurationValid,
    };
  }, [mediaMetadata, maxImages]);

  return { validation, requirements: REQUIREMENTS };
}
